import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ProgressTracker = ({ progress, achievements, streaks }) => {
  const getProgressColor = (percentage) => {
    if (percentage >= 80) return 'from-success to-success/80';
    if (percentage >= 60) return 'from-accent to-accent/80';
    if (percentage >= 40) return 'from-warning to-warning/80';
    return 'from-muted-foreground to-muted-foreground/80';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground mb-2">Learning Progress</h2>
          <p className="text-muted-foreground">Track your journey and celebrate milestones</p>
        </div>
        <Button
          variant="outline"
          iconName="Download"
          iconPosition="left"
        >
          Export Report
        </Button>
      </div>

      {/* Overall Progress */}
      <div className="holographic-card p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-foreground">Overall Progress</h3>
          <div className="text-right">
            <div className="text-2xl font-bold text-accent">{progress.overall}%</div>
            <div className="text-sm text-muted-foreground">Complete</div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {progress.tracks.map((track, index) => (
            <div key={index} className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Icon name={track.icon} size={20} className={track.color} />
                  <span className="font-medium text-foreground">{track.name}</span>
                </div>
                <span className="text-sm font-medium text-foreground">{track.progress}%</span>
              </div>

              <div className="relative">
                <div className="w-full bg-muted rounded-full h-3">
                  <div 
                    className={`h-3 rounded-full bg-gradient-to-r ${getProgressColor(track.progress)}`}
                    style={{ width: `${track.progress}%` }}
                  ></div>
                </div>
                <div className="absolute inset-0 rounded-full bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Completed</span>
                  <span className="text-foreground">{track.completed}/{track.total} courses</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Time Spent</span>
                  <span className="text-foreground">{track.timeSpent}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Learning Streaks */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="holographic-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground">Learning Streaks</h3>
            <Icon name="Flame" size={24} className="text-warning" />
          </div>

          <div className="space-y-4">
            <div className="text-center p-4 rounded-lg bg-warning/10 border border-warning/20">
              <div className="text-3xl font-bold text-warning mb-1">{streaks.current}</div>
              <div className="text-sm text-muted-foreground">Current Streak</div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-3 rounded-lg bg-muted/20">
                <div className="text-xl font-bold text-foreground">{streaks.longest}</div>
                <div className="text-xs text-muted-foreground">Longest Streak</div>
              </div>
              <div className="text-center p-3 rounded-lg bg-muted/20">
                <div className="text-xl font-bold text-foreground">{streaks.thisWeek}</div>
                <div className="text-xs text-muted-foreground">This Week</div>
              </div>
            </div>

            {/* Weekly Activity */}
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-foreground">This Week</h4>
              <div className="flex justify-between">
                {streaks.weeklyActivity.map((day, index) => (
                  <div key={index} className="flex flex-col items-center space-y-1">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      day.active ? 'bg-success text-white' : 'bg-muted/30 text-muted-foreground'
                    }`}>
                      {day.active && <Icon name="Check" size={16} />}
                    </div>
                    <span className="text-xs text-muted-foreground">{day.day}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Recent Achievements */}
        <div className="holographic-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground">Recent Achievements</h3>
            <Button
              variant="ghost"
              size="sm"
              iconName="ChevronRight"
              iconPosition="right"
            >
              View All
            </Button>
          </div>

          <div className="space-y-3">
            {achievements.recent.map((achievement, index) => (
              <div key={index} className="flex items-center space-x-3 p-3 rounded-lg bg-muted/20 hover:bg-muted/30 transition-colors duration-300">
                <div className={`w-10 h-10 rounded-lg ${achievement.bgColor} flex items-center justify-center shadow-glow`}>
                  <Icon name={achievement.icon} size={20} color="white" />
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-foreground">{achievement.title}</h4>
                  <p className="text-sm text-muted-foreground">{achievement.description}</p>
                </div>
                <div className="text-xs text-muted-foreground">{achievement.date}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Skill Development Chart */}
      <div className="holographic-card p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-foreground">Skill Development Over Time</h3>
          <div className="flex space-x-2">
            <Button variant="ghost" size="sm">1M</Button>
            <Button variant="ghost" size="sm">3M</Button>
            <Button variant="outline" size="sm">6M</Button>
            <Button variant="ghost" size="sm">1Y</Button>
          </div>
        </div>

        {/* Simplified Chart Representation */}
        <div className="space-y-4">
          {progress.skillDevelopment.map((skill, index) => (
            <div key={index} className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-foreground">{skill.name}</span>
                <span className="text-sm text-muted-foreground">+{skill.improvement}% this month</span>
              </div>
              <div className="relative h-2 bg-muted rounded-full overflow-hidden">
                <div 
                  className={`h-full bg-gradient-to-r ${skill.color} transition-all duration-1000`}
                  style={{ width: `${skill.level}%` }}
                ></div>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Milestones */}
      <div className="holographic-card p-6">
        <h3 className="text-lg font-semibold text-foreground mb-6">Upcoming Milestones</h3>
        
        <div className="space-y-4">
          {progress.milestones.map((milestone, index) => (
            <div key={index} className="flex items-center space-x-4 p-4 rounded-lg bg-muted/20">
              <div className={`w-12 h-12 rounded-lg ${milestone.bgColor} flex items-center justify-center shadow-glow`}>
                <Icon name={milestone.icon} size={24} color="white" />
              </div>
              <div className="flex-1">
                <h4 className="font-medium text-foreground mb-1">{milestone.title}</h4>
                <p className="text-sm text-muted-foreground mb-2">{milestone.description}</p>
                <div className="flex items-center space-x-4">
                  <div className="flex-1">
                    <div className="w-full bg-muted rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full bg-gradient-to-r ${milestone.progressColor}`}
                        style={{ width: `${milestone.progress}%` }}
                      ></div>
                    </div>
                  </div>
                  <span className="text-sm font-medium text-foreground">{milestone.progress}%</span>
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm font-medium text-foreground">{milestone.eta}</div>
                <div className="text-xs text-muted-foreground">ETA</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProgressTracker;